import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Car, Shield, Clock, MapPin } from 'lucide-react';
import { ReviewsSection } from '@/components/ReviewsSection';
import { FAQSection } from '@/components/FAQSection';
import { HowItWorks } from '@/components/HowItWorks';
import { StatsSection } from '@/components/StatsSection';
import { CitiesSection } from '@/components/CitiesSection';

const HomePage = () => {
  return (
    <div>
      <Navbar />
      
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <img
          src="https://images.unsplash.com/photo-1770899235413-3c75535b018a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxwYWtpc3RhbiUyMGthcmFrb3JhbSUyMGhpZ2h3YXl8ZW58MHx8fHwxNzg0MDY0ODgwfDA&ixlib=rb-4.1.0&q=85"
          alt="Karakoram Highway"
          className="hero-background"
        />
        <div className="hero-overlay"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-white">
          <div className="max-w-2xl fade-in">
            <p className="text-sm uppercase tracking-[0.2em] mb-4 font-mono opacity-90">Premium Car Rentals</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }} data-testid="hero-title">
              Explore Pakistan in Comfort
            </h1>
            <p className="text-lg sm:text-xl mb-8 opacity-90" data-testid="hero-subtitle">
              From the mountains of Gilgit to the streets of Karachi, drive your dream car anywhere in Pakistan.
            </p>
            <div className="flex gap-4">
              <Link to="/cars" data-testid="hero-browse-btn">
                <Button className="btn-primary text-lg px-8 py-6">
                  Browse Cars
                </Button>
              </Link>
              <Link to="/register" data-testid="hero-signup-btn">
                <Button className="btn-secondary text-lg px-8 py-6 bg-white/10 hover:bg-white/20 text-white border-white">
                  Sign Up
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-muted" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] mb-3 font-mono text-accent">Why Choose Us</p>
            <h2 className="text-4xl font-bold" style={{ fontFamily: 'Cabinet Grotesk' }}>Your Journey, Our Priority</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-card p-8 rounded-lg border border-border fade-in stagger-item" data-testid="feature-wide-selection">
              <Car className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-bold mb-3">Wide Selection</h3>
              <p className="text-muted-foreground">From economy to luxury, find the perfect car for your journey.</p>
            </div>
            
            <div className="bg-card p-8 rounded-lg border border-border fade-in stagger-item" data-testid="feature-best-prices">
              <Shield className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-bold mb-3">Best Prices</h3>
              <p className="text-muted-foreground">Competitive rates with no hidden charges.</p>
            </div>
            
            <div className="bg-card p-8 rounded-lg border border-border fade-in stagger-item" data-testid="feature-easy-booking">
              <Clock className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-bold mb-3">Easy Booking</h3>
              <p className="text-muted-foreground">Book your car in minutes with our simple process.</p>
            </div>
            
            <div className="bg-card p-8 rounded-lg border border-border fade-in stagger-item" data-testid="feature-nationwide">
              <MapPin className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-bold mb-3">Nationwide Service</h3>
              <p className="text-muted-foreground">Available in major cities across Pakistan.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* How It Works */}
      <HowItWorks />

      {/* Cities Section */}
      <CitiesSection />

      {/* Reviews Section */}
      <ReviewsSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section className="py-24 px-6 bg-primary text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Ready to Hit the Road?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Browse our collection and book your perfect ride today.
          </p>
          <Link to="/cars" data-testid="cta-browse-btn">
            <Button className="bg-accent hover:bg-accent/90 text-white text-lg px-8 py-6 rounded-full">
              Explore Our Fleet
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
